import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import TableBody from "./TableBody";
import TableHeader from "./TableHeader";
import { ContainerWithLoader, FilterSearch, PaginationWithLimit } from "../../../../components/ui";

import {
  FUNDED_TRADER_STATUS,
  FUNDED_TRADERS_FILTER_OPTIONS,
  STATUSES_COLORS,
} from "../../constants";
import newSortBy from "../../../../utils/newSortBy";
import usePagination from "../../../../hooks/usePagination";
import { APIFunds } from "../../../../api";
import { StyledTable } from "../../../../useGeneralStyles";
import { ActiveFilter } from "../../../../components/ui/FilterSearch";
import StatusColorLegend from "../../../../components/ui/StatusColorLegend";
import { FUNDS_API_URLS } from "../../../../api/funds";
import { fundedTradersActions } from "../../../../reducers/fundedTraders";
import { ISearchAndPaginateData } from "../../../../api/funds/index.types";
import ProcessingStatusFilterOptions from "../ProcessingStatusFilterOptions";

interface IProps {
  account_type?: ISearchAndPaginateData["account_type"];
  evaluation_type?: ISearchAndPaginateData["evaluation_type"];
}

const FundedTradersTable: React.FC<IProps> = ({ account_type, evaluation_type }) => {
  const dispatch = useDispatch();
  const { limit, setLimit, setPage, page, setCount } = usePagination();
  const { loading: fundedTradersStatusesListLoading } = useSelector(
    (state) => state.fundedTradersStatusesList
  );

  const [activeFilters, setActiveFilters] = useState<Record<string, ActiveFilter>>({});

  const [processing_status, setProcessingStatus] = useState<string[]>([]);

  const isActiveLiveSimOrSimTab = evaluation_type === "LiveSim" || evaluation_type === "Live";
  const [sortBy, setSortBy] = useState(
    isActiveLiveSimOrSimTab
      ? {
          by: "invoice_status",
          order: true,
        }
      : {
          by: "id",
          order: false,
        }
  );

  const {
    data: { funded_traders, count },
    isLoading,
  } = useSelector((state) => state.fundedTraders);

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  const { isError, error } = useQuery({
    queryKey: [FUNDS_API_URLS.funds, page, limit, sortBy, processing_status, activeFilters],
    queryFn: async ({ signal }) => {
      dispatch(fundedTradersActions.fetchFailedPaymentsRequest());

      const { data } = await APIFunds.getFundedTraders(
        {
          account_type,
          evaluation_type,
          limit: +limit,
          page,
          sortBy,
          activeFilters,
          processing_status,
        },
        signal
      );
      // dispatch(ForcedToLiveAccounts.fetchForcedToLiveAccountsSuccess(data.forced_to_live));
      dispatch(
        fundedTradersActions.fetchFailedPaymentsSuccess({
          funded_traders: data.funded_traders.rows,
          count: data.funded_traders.count,
        })
      );
      setCount(data.funded_traders.count);
      return data;
    },
  });

  useEffect(() => {
    if (isError && error) {
      console.error("Error fetching funded traders", error);
      dispatch(fundedTradersActions.fetchFailedPaymentsFailed());
    }
  }, [isError, error]);

  return (
    <div>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <FilterSearch
          placeholder="Type to search Name, or click to select filter..."
          handleSearch={setActiveFilters}
          filterOptions={FUNDED_TRADERS_FILTER_OPTIONS}
          defaultSearchFilters={["name"]}
        />

        <Box sx={{ width: "250px", mb: "10px", ml: "20px" }}>
          <ProcessingStatusFilterOptions handleSearchChange={setProcessingStatus} />
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: "10px" }}>
        <Box sx={{ display: "flex", alignItems: "center", ml: "20px" }}>
          <span>Funded trader status legend:</span>
          <StatusColorLegend legend={FUNDED_TRADER_STATUS} />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", ml: "20px" }}>
          <span>Invoice and processing status legend:</span>
          <StatusColorLegend legend={STATUSES_COLORS} />
        </Box>
      </Box>

      <ContainerWithLoader
        isLoading={isLoading || fundedTradersStatusesListLoading}
        report={funded_traders}
      >
        <Box sx={{ overflowX: "scroll" }}>
          <StyledTable>
            <TableHeader sortBy={sortBy} handleSortBy={handleSortBy} />
            <TableBody data-test="funded-traders-table" />
          </StyledTable>
        </Box>
      </ContainerWithLoader>

      <div>
        <PaginationWithLimit
          count={count}
          limit={limit}
          page={page}
          setLimit={setLimit}
          setPageState={setPage}
        />
      </div>
    </div>
  );
};

export default FundedTradersTable;
